import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Users, Check, Loader2, Wind, AlertTriangle,
  TrendingUp, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';

const TEST_TYPES = [
  { value: 'YOYO', label: 'Yo-Yo Test' },
  { value: 'GACON', label: 'Gacon Test' },
];
const YOYO_PROTOCOLS = ['Yo-Yo IR1', 'Yo-Yo IR2'];
const TEST_LABELS = { YOYO: 'Yo-Yo Test', GACON: 'Gacon Test' };

export default function GroupAerobicTestPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;

  const [step, setStep] = useState('setup');
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Setup
  const [teamId, setTeamId] = useState('');
  const [testType, setTestType] = useState('');
  const [protocol, setProtocol] = useState('');
  const [testDate, setTestDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [selectedAthletes, setSelectedAthletes] = useState([]);

  // Testing
  const [sessionId, setSessionId] = useState(null);
  const [entries, setEntries] = useState({});

  // Results
  const [finalResult, setFinalResult] = useState(null);

  useEffect(() => { api.get('/teams').then(r => setTeams(r.data)).catch(() => {}); }, [api]);

  useEffect(() => {
    if (!teamId) return;
    api.get(`/athletes?team_id=${teamId}`).then(r => {
      setAthletes(r.data);
      setSelectedAthletes(r.data.map(a => a.id));
    }).catch(() => {});
  }, [api, teamId]);

  const toggleAthlete = (id) => {
    setSelectedAthletes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const startSession = async () => {
    if (!teamId || !testType || selectedAthletes.length === 0) {
      toast.error(t('Select team, type, and athletes', 'Seleziona squadra, tipo e atleti'));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post('/aerobic/sessions', {
        team_id: teamId, date: testDate, test_type: testType,
        protocol_name: testType === 'YOYO' ? protocol : null,
        notes, athlete_ids: selectedAthletes,
      });
      setSessionId(r.data.id);
      const init = {};
      selectedAthletes.forEach(aid => {
        init[aid] = { speed: '', distance: '', mas: '', level: '', hr: '' };
      });
      setEntries(init);
      setStep('testing');
      toast.success(t('Session created', 'Sessione creata'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const saveResults = async () => {
    const results = [];
    for (const [aid, d] of Object.entries(entries)) {
      const speed = parseFloat(d.speed);
      const dist = parseFloat(d.distance);
      const mas = parseFloat(d.mas);
      if (isNaN(speed) || isNaN(dist) || isNaN(mas)) continue;
      const entry = {
        athlete_id: aid,
        final_speed_kmh: speed,
        total_distance_m: dist,
        mas_vam_kmh: mas,
      };
      if (d.level) entry.level_reached = d.level;
      if (d.hr) entry.heart_rate_bpm = parseInt(d.hr) || null;
      results.push(entry);
    }
    if (results.length === 0) {
      toast.error(t('Enter data for at least one athlete', 'Inserisci dati per almeno un atleta'));
      return;
    }
    setLoading(true);
    try {
      await api.post(`/aerobic/sessions/${sessionId}/results/batch`, { results });
      const fin = await api.post(`/aerobic/sessions/${sessionId}/finalize`);
      setFinalResult(fin.data);
      setStep('results');
      toast.success(t('Session finalized', 'Sessione finalizzata'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const activeAthletes = useMemo(() => athletes.filter(a => selectedAthletes.includes(a.id)), [athletes, selectedAthletes]);
  const isYoYo = testType === 'YOYO';

  // ─── SETUP ────────────────────────
  if (step === 'setup') {
    return (
      <div className="space-y-6 fade-in" data-testid="aerobic-setup">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/staff/aerobic')} className="text-zinc-400 hover:text-zinc-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
              <Wind className="w-6 h-6 text-teal-400" /> {t('Group Aerobic Test', 'Test Aerobico di Gruppo')}
            </h1>
            <p className="text-sm text-zinc-500">{t('Step 1 — Setup', 'Fase 1 — Impostazione')}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-team">
              <SelectValue placeholder={t('Select Team', 'Squadra')} />
            </SelectTrigger>
            <SelectContent>{teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code} — {tm.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={testType} onValueChange={v => { setTestType(v); if (v !== 'YOYO') setProtocol(''); }}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-type">
              <SelectValue placeholder={t('Test Type', 'Tipo')} />
            </SelectTrigger>
            <SelectContent>{TEST_TYPES.map(tt => <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>)}</SelectContent>
          </Select>
          {testType === 'YOYO' && (
            <Select value={protocol} onValueChange={setProtocol}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-protocol">
                <SelectValue placeholder={t('Protocol', 'Protocollo')} />
              </SelectTrigger>
              <SelectContent>{YOYO_PROTOCOLS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Input type="date" value={testDate} onChange={e => setTestDate(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-date" />
        </div>
        <Input placeholder={t('Notes (optional)', 'Note')} value={notes} onChange={e => setNotes(e.target.value)}
          className="bg-zinc-900 border-zinc-700 text-zinc-200" />

        {athletes.length > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-400" /> {t('Athletes', 'Atleti')} ({selectedAthletes.length}/{athletes.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {athletes.map(a => (
                  <button key={a.id} onClick={() => toggleAthlete(a.id)}
                    className={`p-3 rounded-sm border text-left transition-all ${
                      selectedAthletes.includes(a.id) ? 'border-teal-500/50 bg-teal-500/10 text-zinc-100' : 'border-zinc-800 bg-zinc-950/50 text-zinc-500'
                    }`} data-testid={`athlete-select-${a.id}`}>
                    <div className="text-sm font-medium">{a.first_name} {a.last_name}</div>
                    <div className="text-xs mt-0.5">{a.primary_position}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Button onClick={startSession} disabled={!teamId || !testType || selectedAthletes.length === 0 || loading}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base" data-testid="start-session-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>{t('Start Testing', 'Inizia Test')} ({selectedAthletes.length} {t('athletes', 'atleti')})</>
          )}
        </Button>
      </div>
    );
  }

  // ─── TESTING TABLE ────────────────
  if (step === 'testing') {
    return (
      <div className="space-y-5 fade-in" data-testid="aerobic-testing">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
              <Wind className="w-5 h-5 text-teal-400" /> {TEST_LABELS[testType]} {protocol && <span className="text-teal-400 text-sm font-normal">({protocol})</span>}
            </h1>
            <p className="text-sm text-zinc-500">{testDate} • {activeAthletes.length} {t('athletes', 'atleti')}</p>
          </div>
          <Button onClick={saveResults} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {t('Save & Finalize', 'Salva e Finalizza')}
          </Button>
        </div>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-3 text-xs text-zinc-500 uppercase">{t('Athlete', 'Atleta')}</th>
                  {isYoYo && <th className="text-center p-3 text-xs text-zinc-500 uppercase">{t('Level', 'Livello')}</th>}
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase">{t('Speed (km/h)', 'Velocita')}</th>
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase">{t('Distance (m)', 'Distanza')}</th>
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase">MAS/VAM (km/h)</th>
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase">HR (bpm)</th>
                </tr>
              </thead>
              <tbody>
                {activeAthletes.map(a => {
                  const e = entries[a.id] || {};
                  const upd = (field, val) => setEntries(prev => ({ ...prev, [a.id]: { ...prev[a.id], [field]: val } }));
                  return (
                    <tr key={a.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                      <td className="p-3">
                        <div className="text-sm font-medium text-zinc-200">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-zinc-500">{a.primary_position}</div>
                      </td>
                      {isYoYo && (
                        <td className="p-3"><Input value={e.level || ''} onChange={ev => upd('level', ev.target.value)}
                          placeholder="17.4" className="w-20 mx-auto bg-zinc-950 border-zinc-700 text-center h-9 text-zinc-100" data-testid={`level-${a.id}`} /></td>
                      )}
                      <td className="p-3"><Input type="number" step="0.1" value={e.speed || ''} onChange={ev => upd('speed', ev.target.value)}
                        placeholder="0.0" className="w-20 mx-auto bg-zinc-950 border-zinc-700 text-center h-9 text-zinc-100" data-testid={`speed-${a.id}`} /></td>
                      <td className="p-3"><Input type="number" step="1" value={e.distance || ''} onChange={ev => upd('distance', ev.target.value)}
                        placeholder="0" className="w-24 mx-auto bg-zinc-950 border-zinc-700 text-center h-9 text-zinc-100" data-testid={`distance-${a.id}`} /></td>
                      <td className="p-3"><Input type="number" step="0.01" value={e.mas || ''} onChange={ev => upd('mas', ev.target.value)}
                        placeholder="0.00" className="w-20 mx-auto bg-zinc-950 border-zinc-700 text-center h-9 text-teal-300 font-semibold" data-testid={`mas-${a.id}`} /></td>
                      <td className="p-3"><Input type="number" step="1" value={e.hr || ''} onChange={ev => upd('hr', ev.target.value)}
                        placeholder="" className="w-20 mx-auto bg-zinc-950 border-zinc-700 text-center h-9 text-zinc-100" data-testid={`hr-${a.id}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── RESULTS ──────────────────────
  if (step === 'results' && finalResult) {
    const s = finalResult.summary;
    return (
      <div className="space-y-6 fade-in" data-testid="aerobic-results">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
              <Wind className="w-6 h-6 text-teal-400" /> {t('Results', 'Risultati')} — {TEST_LABELS[testType]} {protocol || ''}
            </h1>
            <p className="text-sm text-zinc-500">{testDate} • {s.athletes_tested} {t('athletes', 'atleti')}</p>
          </div>
          <Button onClick={() => navigate('/staff/aerobic')} className="bg-zinc-700 hover:bg-zinc-600 text-white" data-testid="back-btn">
            {t('Back to Dashboard', 'Dashboard')}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.team_avg_mas != null && (
            <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-teal-400">{s.team_avg_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Team Avg MAS', 'MAS Media')}</div>
            </CardContent></Card>
          )}
          {s.best_mas != null && (
            <Card className="bg-emerald-500/10 border-emerald-500/30"><CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-emerald-400">{s.best_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Best MAS', 'MAS Migliore')}</div>
            </CardContent></Card>
          )}
          {s.lowest_mas != null && (
            <Card className="bg-red-500/10 border-red-500/30"><CardContent className="p-4 text-center">
              <TrendingDown className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-red-400">{s.lowest_mas} km/h</div>
              <div className="text-xs text-zinc-500">{t('Lowest MAS', 'MAS Minore')}</div>
            </CardContent></Card>
          )}
        </div>

        {finalResult.alerts.length > 0 && (
          <Card className="bg-red-500/5 border-red-500/30">
            <CardHeader className="pb-2"><CardTitle className="text-red-400 text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {t('Alerts', 'Allerte')} ({finalResult.alerts.length})
            </CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {finalResult.alerts.map((a, i) => (
                <div key={i} className="flex justify-between p-2 bg-red-500/5 rounded-sm text-sm" data-testid={`result-alert-${i}`}>
                  <span className="text-zinc-200">{a.athlete_name}</span>
                  <span className="text-red-400 text-xs">
                    {a.type === 'PERFORMANCE_DROP' && `MAS ${t('drop','calo')} ${a.drop_pct}%`}
                    {a.type === 'BELOW_TEAM_AVG' && `${a.below_pct}% ${t('below avg','sotto media')}`}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-zinc-100 text-base">{t('Athlete Results', 'Risultati')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.values(s.athlete_summaries || {})
              .sort((a, b) => (b.mas_vam_kmh || 0) - (a.mas_vam_kmh || 0))
              .map((as, i) => (
                <div key={as.athlete_id} className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800" data-testid={`result-athlete-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'}`}>{i + 1}</span>
                    <span className="text-sm font-medium text-zinc-200">{as.athlete_name || as.athlete_id?.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-teal-400 font-semibold">{as.mas_vam_kmh} km/h</span>
                    {as.total_distance_m && <span className="text-zinc-400">{as.total_distance_m}m</span>}
                    {as.heart_rate_bpm && <span className="text-zinc-500 text-xs">{as.heart_rate_bpm} bpm</span>}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
